import { useState, useEffect, useCallback } from 'preact/hooks';
import {
  fetchTierQuotas,
  upsertTierQuota,
  deleteFeatureQuota,
  type TierQuotaRow,
} from '../api/admin';

const TIERS = ['free', 'pro', 'managed'] as const;

const thStyle: Record<string, string | number> = { padding: '8px 6px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' };
const tdStyle: Record<string, string | number> = { padding: '8px 6px', verticalAlign: 'middle' };
const addBtnStyle: Record<string, string | number> = { background: 'var(--theme-accent)', color: 'var(--text-on-accent, #fff)', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' };

interface CellData {
  limitValue: number | null;
  enabled: boolean;
  period: string;
  description: string | null;
}

export function TierQuotasTab() {
  const [quotas, setQuotas] = useState<Record<string, TierQuotaRow[]>>({});
  const [featureKeys, setFeatureKeys] = useState<string[]>([]);
  const [editing, setEditing] = useState<string | null>(null); // "tier:featureKey"
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    fetchTierQuotas()
      .then((r) => {
        setQuotas(r.quotas);
        setFeatureKeys(r.featureKeys);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  function getCell(tier: string, featureKey: string): CellData {
    const rows = quotas[tier] || [];
    const row = rows.find((r) => r.featureKey === featureKey);
    return row
      ? { limitValue: row.limitValue, enabled: row.enabled, period: row.period, description: row.description }
      : { limitValue: null, enabled: true, period: 'forever', description: null };
  }

  function formatLimit(val: number | null): string {
    return val === null ? '∞' : String(val);
  }

  async function handleSave(tier: string, featureKey: string, raw: string) {
    setSaving(true);
    setError('');
    try {
      const limitValue = raw.trim() === '∞' || raw.trim() === '' ? null : parseInt(raw, 10);
      if (limitValue !== null && isNaN(limitValue)) {
        setError('Invalid number. Use a number or ∞ for unlimited.');
        setSaving(false);
        return;
      }
      const cell = getCell(tier, featureKey);
      await upsertTierQuota({ tier, featureKey, limitValue, enabled: cell.enabled, period: cell.period, description: cell.description ?? undefined });
      setEditing(null);
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function handleToggle(tier: string, featureKey: string) {
    const cell = getCell(tier, featureKey);
    setSaving(true);
    try {
      await upsertTierQuota({ tier, featureKey, limitValue: cell.limitValue, enabled: !cell.enabled, period: cell.period, description: cell.description ?? undefined });
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function handleAddFeature() {
    const key = newFeature.trim().replace(/\s+/g, '_').toLowerCase();
    if (!key) return;
    setSaving(true);
    try {
      for (const tier of TIERS) {
        await upsertTierQuota({ tier, featureKey: key, limitValue: null, enabled: true, period: 'forever', description: key });
      }
      setNewFeature('');
      setShowAdd(false);
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function handleDeleteFeature(featureKey: string) {
    if (!confirm(`Delete "${featureKey}" across all tiers?`)) return;
    try {
      await deleteFeatureQuota(featureKey);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <div class="admin-content" style={{ padding: '8px 0' }}>
      {error && <div class="message error" style={{ margin: '8px 16px' }}>{error}</div>}

      {/* Matrix table */}
      <div style={{ overflowX: 'auto', padding: '0 8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <th style={thStyle}>Feature</th>
              {TIERS.map((t) => (
                <th key={t} style={{ ...thStyle, textTransform: 'capitalize' }}>{t}</th>
              ))}
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {featureKeys.map((fk) => (
              <tr key={fk} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 500 }}>{fk.replace(/_/g, ' ')}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>
                    {getCell('free', fk).period} · {getCell('free', fk).description || ''}
                  </div>
                </td>
                {TIERS.map((tier) => {
                  const cell = getCell(tier, fk);
                  const cellKey = `${tier}:${fk}`;
                  const isEditing = editing === cellKey;
                  return (
                    <td key={tier} style={{ ...tdStyle, textAlign: 'center', minWidth: 70 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValue}
                            onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSave(tier, fk, editValue);
                              if (e.key === 'Escape') setEditing(null);
                            }}
                            onBlur={() => handleSave(tier, fk, editValue)}
                            autoFocus
                            style={{ width: 50, textAlign: 'center', background: 'var(--bg-elevated)', border: '1px solid var(--theme-accent)', borderRadius: 4, color: 'var(--text-primary)', padding: '2px 4px', fontSize: 13 }}
                          />
                        ) : (
                          <span
                            onClick={() => { setEditing(cellKey); setEditValue(formatLimit(cell.limitValue)); }}
                            style={{ cursor: 'pointer', padding: '2px 8px', borderRadius: 4, background: cell.enabled ? 'var(--bg-elevated)' : 'color-mix(in srgb, var(--accent-red) 14%, var(--bg-elevated))', color: cell.limitValue === null ? 'var(--accent-green)' : 'var(--text-secondary)', fontWeight: 500, fontSize: 14 }}
                          >
                            {formatLimit(cell.limitValue)}
                          </span>
                        )}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-dim)', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={cell.enabled}
                            onChange={() => handleToggle(tier, fk)}
                            style={{ width: 12, height: 12 }}
                          />
                          on
                        </label>
                      </div>
                    </td>
                  );
                })}
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <button
                    onClick={() => handleDeleteFeature(fk)}
                    style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', fontSize: 14 }}
                    title="Delete feature"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add feature */}
      <div style={{ padding: '12px 16px' }}>
        {showAdd ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={newFeature}
              onInput={(e) => setNewFeature((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddFeature(); if (e.key === 'Escape') setShowAdd(false); }}
              placeholder="feature_key"
              autoFocus
              style={{ flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border-medium)', borderRadius: 4, color: 'var(--text-primary)', padding: '6px 8px', fontSize: 13 }}
            />
            <button onClick={handleAddFeature} disabled={saving} style={addBtnStyle}>Add</button>
            <button onClick={() => setShowAdd(false)} style={{ ...addBtnStyle, background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setShowAdd(true)} style={addBtnStyle}>+ Add Feature</button>
        )}
      </div>
    </div>
  );
}
